import { useState, type MouseEvent } from "react";
import { ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import type { ProgramNewsItem, ProgramNewsItemStatus } from "@/services/adminProgramNews";
import ArticleDetail from "./ArticleDetail";
import ArticleDetailSheet from "./ArticleDetailSheet";
import ReviewedBadge from "./ReviewedBadge";
import ReasonLine from "./ReasonLine";
import Term from "./Term";
import { articleStatusTerm } from "./glossary";
import { formatDate } from "./formatters";
import { formatSchoolName, splitTeamId } from "./teamLabel";

interface ArticleRowProps {
  item: ProgramNewsItem;
  onReviewChanged?: () => void;
}

const noop = () => {};

const STATUS_VARIANT: Record<
  ProgramNewsItemStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  new: "secondary",
  suppressed: "outline",
  classified: "default",
  failed: "destructive",
};

const ArticleRow = ({ item, onReviewChanged = noop }: ArticleRowProps) => {
  const [open, setOpen] = useState(false);
  const { slug, gender } = splitTeamId(item.teamId);

  return (
    <TableRow
      className="cursor-pointer"
      onClick={(event: MouseEvent<HTMLElement>) => {
        if (!(event.target as HTMLElement).closest("a,button")) setOpen(true);
      }}
    >
      <TableCell className="text-sm whitespace-nowrap">{formatDate(item.publishedAt)}</TableCell>
      <TableCell>
        <div className="font-medium" title={item.teamId}>
          {formatSchoolName(slug)}
        </div>
        <div className="text-xs text-muted-foreground">{gender}</div>
      </TableCell>
      <TableCell className="text-sm">
        {item.sourceUrl ? (
          <a
            href={item.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={item.sourceUrl}
            className="inline-flex items-center gap-1 text-primary-text hover:underline"
          >
            {item.title}
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        ) : (
          item.title
        )}
        <ReasonLine item={item} />
        <ReviewedBadge verdict={item.myVerdict} />
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANT[item.processingStatus] ?? "outline"}>
          <Term term={articleStatusTerm(item.processingStatus)}>{item.processingStatus}</Term>
        </Badge>
      </TableCell>
      <TableCell>
        <ArticleDetail item={item} />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-2"
          aria-label={`Review ${item.title}`}
          onClick={() => setOpen(true)}
        >
          Review
        </Button>
        {open ? (
          <ArticleDetailSheet
            item={item}
            open={open}
            onOpenChange={setOpen}
            onReviewChanged={onReviewChanged}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
};

export default ArticleRow;
